import { SyntheticEvent, useState } from 'react';
import QuickSightDashboardEmbed from '@common/components/dashboard/QuickSightDashboardEmbed';
import useAuth from '@common/hooks/useAuth';
import { getQuickSightDashboard } from '@common/utils/getQuickSightDashboard';
import { format, set, setISODay } from 'date-fns';
import { Role } from '@common/@types/auth';
import {
  Box,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  ToggleButton,
  Typography,
} from '@mui/material';
import { ToggleWrapper } from '@common/components/ToggleWrapper';

const levelMapping: { [key: string]: string[] } = {
  'LL & Linc': ['LL', 'Linc'],
  'L1 & L2': ['L1', 'L2'],
  L4: ['L4.1', 'L4.2', 'L4.3', 'L4.4'],
  LL: ['LL'],
  Linc: ['Linc'],
  L1: ['L1'],
  L2: ['L2'],
  L3: ['L3'],
  'L4.1': ['L4.1'],
  'L4.2': ['L4.2'],
  'L4.3': ['L4.3'],
  'L4.4': ['L4.4'],
};

const getLevelParameter = (levelString: string): string[] =>
  levelMapping[levelString] || ['ALL_VALUES'];

const subjects: string[] = [
  'American History',
  'Biology',
  'Calculus',
  'Chemistry',
  'Computer Science',
  'Economics',
  'English',
  'Foreign Language',
  'Human Geography',
  'Language',
  'Math',
  'Non-Core Skills',
  'Physics',
  'Reading',
  'SAT Prep',
  'Science',
  'Social Science',
  'Statistics',
  'Test Prep',
  'World History',
  'World Languages',
  'Writing',
];

function useLeaderboardState() {
  const { user } = useAuth();
  const levelString = user?.alphaAuthAttributes?.levels?.[0]?.displayName ?? '';
  const isStudent = user?.alphaAuthAttributes?.role === Role.STUDENT;
  const levelParameter = getLevelParameter(levelString);

  const [period, setPeriod] = useState<'week' | 'session'>('week');
  const [dateRange, setDateRange] = useState({
    startDate: setISODay(new Date(), 1),
    endDate: setISODay(new Date(), 7),
  });

  const handlePeriodChange = (event: SyntheticEvent<Element, Event>, newPeriod: any) => {
    setPeriod(newPeriod);
    const now = new Date();
    if (newPeriod === 'week') {
      setDateRange({
        startDate: setISODay(now, 1),
        endDate: setISODay(now, 7),
      });
    } else {
      setDateRange({
        startDate: set(now, { year: 2023, month: 9, date: 16 }),
        endDate: set(now, { year: 2023, month: 11, date: 24 }),
      });
    }
  };

  const [subject, setSubject] = useState('ALL_VALUES');
  const handleSubjectChange = (event: SelectChangeEvent) => {
    setSubject(event.target.value as string);
  };

  return {
    isStudent,
    levelParameter,
    period,
    dateRange,
    subject,
    handlePeriodChange,
    handleSubjectChange,
  };
}

export default function Leaderboard({ header, studentDashboards, nonStudentDashboards }: Props) {
  const {
    isStudent,
    levelParameter,
    period,
    dateRange,
    subject,
    handlePeriodChange,
    handleSubjectChange,
  } = useLeaderboardState();

  const dashboards = isStudent ? studentDashboards : nonStudentDashboards;
  const baseParameters = [
    { Name: 'StartDate', Values: [format(dateRange.startDate, 'yyyy-MM-dd')] },
    { Name: 'EndDate', Values: [format(dateRange.endDate, 'yyyy-MM-dd')] },
  ];
  const studentParameters = isStudent
    ? [
        { Name: 'Subject', Values: [subject] },
        { Name: 'Level', Values: levelParameter },
      ]
    : [];

  return (
    <>
      <Typography variant="h4" component="h1" gutterBottom>
        {header}
      </Typography>
      <Box display={'flex'} alignItems={'center'} mt={2}>
        <ToggleWrapper
          label={'Period:'}
          value={period}
          clickHandler={handlePeriodChange}
          aria-label="period"
        >
          <ToggleButton value="week" aria-label="week">
            Current Week
          </ToggleButton>
          <ToggleButton value="session" aria-label="session">
            Current Session
          </ToggleButton>
        </ToggleWrapper>
        {isStudent && (
          <Box display="flex" alignItems="center" ml={4}>
            <InputLabel id="subject-label">
              <strong>Subject</strong>
            </InputLabel>
            <Box ml={2}>
              <Select
                labelId="subject-label"
                value={subject}
                onChange={handleSubjectChange}
                displayEmpty
                inputProps={{ 'aria-label': 'Without label' }}
              >
                <MenuItem value="ALL_VALUES">All Subjects</MenuItem>
                {subjects.map((subject) => (
                  <MenuItem key={subject} value={subject}>
                    {subject}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>
        )}
      </Box>
      <>
        {dashboards.map((dashboard) => (
          <QuickSightDashboardEmbed
            key={dashboard}
            dashboardConfig={getQuickSightDashboard(dashboard)}
            parameters={[...baseParameters, ...studentParameters]}
          />
        ))}
      </>
    </>
  );
}

type Props = {
  header: string;
  studentDashboards: string[];
  nonStudentDashboards: string[];
};
